import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  InputAdornment,
  Alert,
  Chip,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Stack,
  alpha
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LockReset as LockResetIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Lock as LockIcon,
  HelpOutline as HelpIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  DriveEta as DriveEtaIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { fetchDrivers, deleteDriver, updateDriver, resetDriverPassword, fetchDriverById } from '../driversApi';
import { fetchCities } from '../citiesApi';
import { useBranch } from '../BranchContext';
import { useTranslation } from 'react-i18next';

const Drivers = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [drivers, setDrivers] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedBranch } = useBranch();
  const [helpOpen, setHelpOpen] = useState(false);
  
  const [editOpen, setEditOpen] = useState(false);
  const [currentDriver, setCurrentDriver] = useState({
    Id: '',
    FullName: '',
    MobileNumber: '',
    Email: '',
    City: ''
  });

  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    Id: '',
    NewPassword: ''
  });

  useEffect(() => {
    loadDrivers();
    loadCities();
    // eslint-disable-next-line
  }, [selectedBranch]);

  const loadDrivers = async () => {
    if (!selectedBranch) {
      setDrivers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchDrivers(selectedBranch.id || selectedBranch.Id);
      setDrivers(data);
    } catch (error) {
      console.error('Error loading drivers:', error);
      Swal.fire(t('error'), t('failedToLoadDrivers'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async () => {
    try {
      const data = await fetchCities();
      setCities(data);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t('areYouSure'),
      text: t('revertWarning'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('yesDeleteIt'),
      cancelButtonText: t('cancel')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteDriver(id);
          Swal.fire(t('deleted'), t('driverDeletedSuccess'), 'success');
          loadDrivers();
        } catch (error) {
          Swal.fire(t('error'), t('failedToDeleteDriver'), 'error');
        }
      }
    });
  };

  const handleEditClick = async (id) => {
    try {
      const driver = await fetchDriverById(id);
      let mobile = driver.mobileNumber || driver.MobileNumber;
      if (mobile && mobile.startsWith('+967')) {
        mobile = mobile.substring(4);
      }
      
      setCurrentDriver({
        Id: driver.id || driver.Id,
        FullName: driver.fullName || driver.FullName,
        MobileNumber: mobile,
        Email: driver.email || driver.Email,
        City: driver.city || driver.City
      });
      setEditOpen(true);
    } catch (error) {
      Swal.fire(t('error'), t('failedToLoadDriverDetails'), 'error');
    }
  };

  const handleEditSave = async () => {
    try {
      const payload = {
        Id: currentDriver.Id,
        FullName: currentDriver.FullName,
        MobileNumber: '+967' + currentDriver.MobileNumber,
        Email: currentDriver.Email,
        City: currentDriver.City
      };
      await updateDriver(currentDriver.Id, payload);
      setEditOpen(false);
      Swal.fire(t('success'), t('driverUpdatedSuccess'), 'success');
      loadDrivers();
    } catch (error) {
      Swal.fire(t('error'), t('failedToUpdateDriver'), 'error');
    }
  };

  const handleResetPasswordClick = (id) => {
    setResetPasswordData({ Id: id, NewPassword: '' });
    setResetPasswordOpen(true);
  };

  const handleResetPasswordSave = async () => {
    if (!resetPasswordData.NewPassword) {
      Swal.fire(t('error'), t('pleaseEnterNewPassword'), 'error');
      return;
    }
    try {
      await resetDriverPassword(resetPasswordData.Id, resetPasswordData.NewPassword);
      setResetPasswordOpen(false);
      Swal.fire(t('success'), t('passwordResetSuccess'), 'success');
    } catch (error) {
      Swal.fire(t('error'), t('failedToResetPassword'), 'error');
    }
  };

  // ✅ Mobile-Optimized Help Dialog
  const HelpDialog = () => {
    const steps = [
      {
        label: t('helpDriversManageStep1Label') || 'View Drivers',
        icon: <DriveEtaIcon color="primary" />,
        description: t('helpDriversManageStep1Description') || 'See all drivers for your branch',
        tips: [
          t('helpDriversManageStep1Tip1') || 'Refresh to see latest data',
          t('helpDriversManageStep1Tip2') || 'Select branch to see drivers'
        ]
      },
      {
        label: t('helpDriversManageStep2Label') || 'Edit Driver',
        icon: <EditIcon color="primary" />,
        description: t('helpDriversManageStep2Description') || 'Update driver information',
        tips: [
          t('helpDriversManageStep2Tip1') || 'Click edit icon to modify',
          t('helpDriversManageStep2Tip2') || 'All changes saved immediately'
        ]
      },
      {
        label: t('helpDriversManageStep3Label') || 'Reset Password',
        icon: <LockResetIcon color="primary" />,
        description: t('helpDriversManageStep3Description') || 'Change driver login password',
        tips: [
          t('helpDriversManageStep3Tip1') || 'Use strong passwords',
          t('helpDriversManageStep3Tip2') || 'Driver will use new password to login'
        ]
      },
      {
        label: t('helpDriversManageStep4Label') || 'Delete Driver',
        icon: <DeleteIcon color="error" />,
        description: t('helpDriversManageStep4Description') || 'Remove driver from system',
        tips: [
          t('helpDriversManageStep4Tip1') || 'Cannot be undone',
          t('helpDriversManageStep4Tip2') || 'Confirm before deleting'
        ]
      }
    ];

    return (
      <Dialog 
        open={helpOpen} 
        onClose={() => setHelpOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 2 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          p: { xs: 2, sm: 2.5 }
        }}>
          <HelpIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {t('helpDriversManageDialogTitle') || 'Driver Management Help'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton 
            onClick={() => setHelpOpen(false)} 
            sx={{ color: 'white' }}
            size={isMobile ? "small" : "medium"}
          >
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, p: { xs: 2, sm: 3 } }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography 
              variant="body2" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
            >
              {t('helpWhatIsDriversManage') || 'Driver Management'}
            </Typography>
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
            >
              {t('helpWhatIsDriversManageDesc') || 'Manage delivery drivers assigned to your branch.'}
            </Typography>
          </Alert>

          <List sx={{ width: '100%' }}>
            {steps.map((step, index) => (
              <Box key={index}>
                <ListItem alignItems="flex-start" sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 0, sm: 2 } }}>
                  <ListItemIcon sx={{ mt: 1, minWidth: { xs: 40, sm: 56 } }}>
                    <Box sx={{ 
                      bgcolor: 'primary.light', 
                      borderRadius: '50%', 
                      p: { xs: 1, sm: 1.5 }, 
                      display: 'flex',
                      boxShadow: 1
                    }}>
                      {React.cloneElement(step.icon, { sx: { fontSize: { xs: 18, sm: 24 } } })}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        color="primary" 
                        sx={{ 
                          mb: 1,
                          fontSize: { xs: '0.95rem', sm: '1.1rem' }
                        }}
                      >
                        {step.label}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mb: 2,
                            fontSize: { xs: '0.8rem', sm: '0.85rem' }
                          }}
                        >
                          {step.description}
                        </Typography>
                        <Box sx={{ bgcolor: '#f5f5f5', p: { xs: 1.5, sm: 2 }, borderRadius: 1 }}>
                          <Typography 
                            variant="caption" 
                            fontWeight="bold" 
                            color="primary" 
                            sx={{ 
                              mb: 1, 
                              display: 'block',
                              fontSize: { xs: '0.75rem', sm: '0.8rem' }
                            }}
                          >
                            💡 {t('helpTips') || 'Tips'}
                          </Typography>
                          {step.tips.map((tip, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                              <CheckCircleIcon sx={{ 
                                fontSize: { xs: 14, sm: 16 }, 
                                mr: 1, 
                                color: 'success.main', 
                                mt: 0.3 
                              }} />
                              <Typography 
                                variant="body2"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
                              >
                                {tip}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < steps.length - 1 && <Divider />}
              </Box>
            ))}
          </List>

          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography 
              variant="body2" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
            >
              ⚠️ {t('helpImportantNotes') || 'Important Notes'}
            </Typography>
            <Typography 
              variant="body2" 
              component="div"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
            >
              <Box sx={{ mt: 1 }}>
                • {t('helpDriversManageImportantNote1') || 'Deleting drivers is permanent'}<br/>
                • {t('helpDriversManageImportantNote2') || 'Reset password carefully'}<br/>
                • {t('helpDriversManageImportantNote3') || 'Drivers need mobile to login'}<br/>
                • {t('helpDriversManageImportantNote4') || 'Keep driver info updated'}
              </Box>
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#f9f9f9' }}>
          <Button 
            onClick={() => setHelpOpen(false)} 
            variant="contained" 
            color="primary" 
            fullWidth={isMobile}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {t('helpGotIt') || 'Got it!'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

    return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      <HelpDialog />

      {/* ✅ Mobile-Optimized Header */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        alignItems={{ xs: 'flex-start', sm: 'center' }} 
        justifyContent="space-between"
        spacing={{ xs: 2, sm: 0 }}
        mb={{ xs: 2.5, sm: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }} flexWrap="wrap">
          <DriveEtaIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
          <Typography 
            variant="h4"
            sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
          >
            {t('manageDrivers') || 'Manage Drivers'}
          </Typography>
          {selectedBranch && (
            <Chip 
              label={`${drivers.length} ${t('drivers') || 'drivers'}`} 
              color="primary"
              size="small"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
            />
          )}
        </Stack>
        
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Tooltip title={t('helpButtonTooltip') || 'Click for help'} arrow>
            <Button
              variant="contained"
              color="info"
              startIcon={<HelpIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
              onClick={() => setHelpOpen(true)}
              fullWidth={isMobile}
              size={isMobile ? "medium" : "large"}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6
                }
              }}
            >
              {t('helpButtonText') || 'Help'}
            </Button>
          </Tooltip>
          <Button
            variant="contained"
            color="success"
            startIcon={<RefreshIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
            onClick={loadDrivers}
            disabled={!selectedBranch}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ 
              borderRadius: 2,
              fontSize: { xs: '0.85rem', sm: '0.9rem' }
            }}
          >
            {t('refresh') || 'Refresh'}
          </Button>
        </Stack>
      </Stack>

      {!selectedBranch && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('pleaseSelectBranchDrivers') || 'Please select a branch first'}
        </Alert>
      )}

      <Card elevation={3} sx={{ borderRadius: { xs: 2, sm: 3 } }}>
        <Box sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          p: { xs: 2, sm: 2.5 }
        }}>
          <Typography 
            variant="h6"
            sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
          >
            {t('driversList') || 'Drivers List'}
          </Typography>
        </Box>
        
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: { xs: 4, sm: 6 } }}>
              <CircularProgress size={isMobile ? 40 : 50} />
            </Box>
          ) : (
            <>
              {/* ✅ Desktop: Table View */}
              {!isMobile && (
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: { sm: '0.85rem', md: '0.9rem' } }}>
                          {t('id') || 'ID'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: { sm: '0.85rem', md: '0.9rem' } }}>
                          {t('fullName') || 'Full Name'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: { sm: '0.85rem', md: '0.9rem' } }}>
                          {t('mobileNumber') || 'Mobile'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: { sm: '0.85rem', md: '0.9rem' } }}>
                          {t('emailAddress') || 'Email'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: { sm: '0.85rem', md: '0.9rem' } }}>
                          {t('city') || 'City'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: { sm: '0.85rem', md: '0.9rem' } }} align="center">
                          {t('actions') || 'Actions'}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {drivers.map((driver) => (
                        <TableRow 
                          key={driver.id || driver.Id}
                          sx={{ 
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <TableCell>
                            <Chip 
                              label={driver.id || driver.Id} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <PersonIcon fontSize="small" color="action" />
                              <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { sm: '0.85rem', md: '0.9rem' } }}>
                                {driver.fullName || driver.FullName}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <PhoneIcon fontSize="small" color="action" />
                              <Typography variant="body2" sx={{ fontSize: { sm: '0.85rem', md: '0.9rem' } }}>
                                {driver.mobileNumber || driver.MobileNumber}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <EmailIcon fontSize="small" color="action" />
                              <Typography variant="body2" sx={{ fontSize: { sm: '0.85rem', md: '0.9rem' } }}>
                                {driver.email || driver.Email || '-'}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={driver.city || driver.City} 
                              size="small" 
                              color="info"
                              icon={<LocationOnIcon sx={{ fontSize: 14 }} />}
                              sx={{ fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={t('editDriver') || 'Edit'} arrow>
                              <IconButton 
                                color="primary" 
                                onClick={() => handleEditClick(driver.id || driver.Id)}
                                size="small"
                              >
                                <EditIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('resetPassword') || 'Reset Password'} arrow>
                              <IconButton 
                                color="warning" 
                                onClick={() => handleResetPasswordClick(driver.id || driver.Id)}
                                size="small"
                              >
                                <LockResetIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('delete') || 'Delete'} arrow>
                              <IconButton 
                                color="error" 
                                onClick={() => handleDelete(driver.id || driver.Id)}
                                size="small"
                              >
                                <DeleteIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {drivers.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                            <DriveEtaIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                              {t('noDriversFound') || 'No drivers found'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* ✅ Mobile: Card View */}
              {isMobile && (
                <Stack spacing={2} sx={{ p: 2 }}>
                  {drivers.map((driver) => (
                    <Paper 
                      key={driver.id || driver.Id}
                      elevation={2}
                      sx={{ 
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.3s',
                        '&:active': {
                          transform: 'scale(0.98)',
                          boxShadow: 1
                        }
                      }}
                    >
                      <Stack spacing={1.5}>
                        {/* Header Row */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Chip 
                            label={`#${driver.id || driver.Id}`} 
                            size="small" 
                            color="primary" 
                            sx={{ fontSize: '0.7rem', height: 22 }}
                          />
                          <Chip 
                            label={driver.city || driver.City} 
                            size="small" 
                            color="info"
                            icon={<LocationOnIcon sx={{ fontSize: 12 }} />}
                            sx={{ fontSize: '0.7rem', height: 22 }}
                          />
                        </Stack>

                        {/* Driver Name */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PersonIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            {driver.fullName || driver.FullName}
                          </Typography>
                        </Stack>

                        {/* Contact Info */}
                        <Stack spacing={0.5}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              {driver.mobileNumber || driver.MobileNumber}
                            </Typography>
                          </Stack>
                          {(driver.email || driver.Email) && (
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }} noWrap>
                                {driver.email || driver.Email}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>

                        <Divider />

                        {/* Action Buttons */}
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                            onClick={() => handleEditClick(driver.id || driver.Id)}
                            fullWidth
                            sx={{ fontSize: '0.75rem' }}
                          >
                            {t('edit') || 'Edit'}
                          </Button>
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            startIcon={<LockResetIcon sx={{ fontSize: 16 }} />}
                            onClick={() => handleResetPasswordClick(driver.id || driver.Id)}
                            fullWidth
                            sx={{ fontSize: '0.75rem' }}
                          >
                            {t('reset') || 'Reset'}
                          </Button>
                          <IconButton 
                            color="error" 
                            onClick={() => handleDelete(driver.id || driver.Id)}
                            size="small"
                          >
                            <DeleteIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}

                  {drivers.length === 0 && !loading && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <DriveEtaIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1rem' }}>
                        {t('noDriversFound') || 'No drivers found'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontSize: '0.75rem' }}>
                        {t('registerDriversFirst') || 'Register drivers to see them here'}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ✅ Mobile-Optimized Edit Driver Dialog */}
      <Dialog 
        open={editOpen} 
        onClose={() => setEditOpen(false)} 
        fullWidth 
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 2 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: { xs: 2, sm: 2.5 }
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <EditIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {t('editDriver') || 'Edit Driver'}
            </Typography>
          </Stack>
          <IconButton 
            onClick={() => setEditOpen(false)} 
            sx={{ color: 'white' }}
            size={isMobile ? "small" : "medium"}
          >
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, p: { xs: 2, sm: 3 } }}>
          <Stack spacing={{ xs: 2, sm: 2.5 }}>
            <TextField
              label={t('fullName') || 'Full Name'}
              fullWidth
              value={currentDriver.FullName}
              onChange={(e) => setCurrentDriver({ ...currentDriver, FullName: e.target.value })}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="primary" sx={{ fontSize: { xs: 18, sm: 20 } }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                }
              }}
            />
            <TextField
              label={t('mobileNumber') || 'Mobile Number'}
              fullWidth
              value={currentDriver.MobileNumber}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 9);
                setCurrentDriver({ ...currentDriver, MobileNumber: val });
              }}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="primary" sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    <Typography sx={{ 
                      ml: 1, 
                      fontWeight: 'bold', 
                      color: 'text.secondary',
                      fontSize: { xs: '0.85rem', sm: '0.9rem' }
                    }}>
                      +967
                    </Typography>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                }
              }}
            />
            <TextField
              label={t('emailAddress') || 'Email'}
              fullWidth
              value={currentDriver.Email}
              onChange={(e) => setCurrentDriver({ ...currentDriver, Email: e.target.value })}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="primary" sx={{ fontSize: { xs: 18, sm: 20 } }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                }
              }}
            />
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel>{t('city') || 'City'}</InputLabel>
              <Select
                value={currentDriver.City}
                label={t('city') || 'City'}
                onChange={(e) => setCurrentDriver({ ...currentDriver, City: e.target.value })}
                startAdornment={
                  <InputAdornment position="start">
                    <LocationOnIcon color="primary" sx={{ fontSize: { xs: 18, sm: 20 } }} />
                  </InputAdornment>
                }
                sx={{
                  '& .MuiSelect-select': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }
                }}
              >
                <MenuItem value="">
                  <em>{t('selectCity') || 'Select city'}</em>
                </MenuItem>
                {cities.map((city) => (
                  <MenuItem 
                    key={city.id || city.Id || city.name || city.Name} 
                    value={city.name || city.Name}
                    sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                  >
                    {city.name || city.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: { xs: 2, sm: 2.5 }, 
          bgcolor: '#f9f9f9',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Button 
            onClick={() => setEditOpen(false)} 
            variant="outlined"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          <Button 
            onClick={handleEditSave} 
            variant="contained" 
            color="primary"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ 
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6
              }
            }}
          >
            {t('saveChanges') || 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Mobile-Optimized Reset Password Dialog */}
      <Dialog 
        open={resetPasswordOpen} 
        onClose={() => setResetPasswordOpen(false)} 
        fullWidth 
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 2 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'warning.main', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: { xs: 2, sm: 2.5 }
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LockResetIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {t('resetDriverPassword') || 'Reset Password'}
            </Typography>
          </Stack>
          <IconButton 
            onClick={() => setResetPasswordOpen(false)} 
            sx={{ color: 'white' }}
            size={isMobile ? "small" : "medium"}
          >
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, p: { xs: 2, sm: 3 } }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
            >
              {t('passwordResetWarning') || 'Driver will use this new password to login to the app.'}
            </Typography>
          </Alert>
          <TextField
            label={t('newPassword') || 'New Password'}
            type="password"
            fullWidth
            value={resetPasswordData.NewPassword}
            onChange={(e) => setResetPasswordData({ ...resetPasswordData, NewPassword: e.target.value })}
            size={isMobile ? "small" : "medium"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="primary" sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }
            }}
          />
        </DialogContent>
        
        <DialogActions sx={{ 
          p: { xs: 2, sm: 2.5 }, 
          bgcolor: '#f9f9f9',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Button 
            onClick={() => setResetPasswordOpen(false)} 
            variant="outlined"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          <Button 
            onClick={handleResetPasswordSave} 
            variant="contained" 
            color="warning"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ 
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6
              }
            }}
          >
            {t('resetPassword') || 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Drivers;